
import React from 'react';
import { Trophy, BookX, Star, Clock, ArrowRight, Activity, TrendingUp, Calendar } from 'lucide-react';
import { User, QuizRecord, Question } from '../data/types';
import { formatTime } from '../utils';

interface DashboardProps {
    user: User;
    quizHistory: QuizRecord[];
    wrongQuestionIds: number[];
    favoriteQuestionIds: number[];
    onNavigate: (view: 'quiz' | 'mistake-book' | 'favorites' | 'learning-map') => void;
    folders: any[];
}

export const DashboardView = ({ user, quizHistory, wrongQuestionIds, favoriteQuestionIds, onNavigate, folders }: DashboardProps) => {
    
    // Calculate Stats
    const totalTests = quizHistory.length;
    const totalScore = quizHistory.reduce((acc, cur) => acc + cur.score, 0);
    const totalPointsPossible = quizHistory.reduce((acc, cur) => acc + cur.totalPoints, 0);
    
    // Avoid division by zero
    const averageScore = totalTests > 0 ? Math.round(totalScore / totalTests) : 0;
    const accuracyRate = totalPointsPossible > 0 ? Math.round((totalScore / totalPointsPossible) * 100) : 0;

    // Get recent history (Last 5)
    const recentHistory = [...quizHistory].sort((a, b) => Number(b.id) - Number(a.id)).slice(0, 5);

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return '早安';
        if (hour < 18) return '午安';
        return '晚安';
    };

    const getFolderName = (folderId: string | null) => {
        if (!folderId) return '綜合測驗';
        const folder = folders.find(f => f.id === folderId);
        return folder ? folder.name : '未知範圍';
    };

    return (
        <div className="flex-1 bg-gray-50 overflow-y-auto p-4 md:p-8">
            <div className="max-w-6xl mx-auto space-y-8">
                
                {/* Welcome Banner */}
                <div className="bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full -mr-16 -mt-16 opacity-50 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-50 rounded-full -ml-12 -mb-12 opacity-50 pointer-events-none"></div>
                    
                    <div className="flex items-center space-x-6 z-10 w-full">
                        <div className="w-20 h-20 md:w-24 md:h-24 bg-blue-100 text-6xl flex items-center justify-center rounded-full border-4 border-white shadow-md">
                            {user.avatar || '🎓'}
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-2">
                                {getGreeting()}，{user.name}！
                            </h1>
                            <p className="text-gray-500">
                                準備好今天的學習挑戰了嗎？累積一點點進步，未來就會大不同。
                            </p>
                        </div>
                    </div>
                    
                    <div className="mt-6 md:mt-0 flex flex-col sm:flex-row gap-3 z-10 w-full md:w-auto">
                        <button 
                            onClick={() => onNavigate('quiz')}
                            className="px-6 py-3 bg-fudan-blue hover:bg-blue-800 text-white rounded-xl font-bold shadow-lg transition-transform transform hover:scale-105 flex items-center justify-center whitespace-nowrap"
                        >
                            開始測驗 <ArrowRight className="w-5 h-5 ml-2" />
                        </button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center">
                        <div className="p-3 bg-blue-50 text-fudan-blue rounded-lg mr-4">
                            <Activity className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-bold">總測驗次數</p>
                            <p className="text-2xl font-black text-gray-800">{totalTests} <span className="text-sm font-normal text-gray-400">次</span></p>
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center">
                        <div className="p-3 bg-green-50 text-green-600 rounded-lg mr-4">
                            <TrendingUp className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-bold">平均分數</p>
                            <p className={`text-2xl font-black ${averageScore >= 80 ? 'text-green-600' : (averageScore >= 60 ? 'text-yellow-600' : 'text-red-500')}`}>
                                {averageScore} <span className="text-sm font-normal text-gray-400">分</span>
                            </p>
                        </div>
                    </div>
                    <div 
                        className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center cursor-pointer hover:border-red-200 transition-colors group"
                        onClick={() => onNavigate('mistake-book')}
                    >
                        <div className="p-3 bg-red-50 text-red-500 rounded-lg mr-4 group-hover:bg-red-100 transition-colors">
                            <BookX className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-bold group-hover:text-red-500">待複習錯題</p>
                            <p className="text-2xl font-black text-gray-800">{wrongQuestionIds.length} <span className="text-sm font-normal text-gray-400">題</span></p>
                        </div>
                    </div>
                    <div 
                        className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center cursor-pointer hover:border-orange-200 transition-colors group"
                        onClick={() => onNavigate('favorites')}
                    >
                        <div className="p-3 bg-orange-50 text-orange-500 rounded-lg mr-4 group-hover:bg-orange-100 transition-colors">
                            <Star className="w-6 h-6 fill-current" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-bold group-hover:text-orange-500">我的最愛</p>
                            <p className="text-2xl font-black text-gray-800">{favoriteQuestionIds.length} <span className="text-sm font-normal text-gray-400">題</span></p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Recent History */}
                    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="font-bold text-gray-800 flex items-center">
                                <Clock className="w-5 h-5 mr-2 text-gray-500" />
                                最近測驗紀錄
                            </h3>
                            {quizHistory.length > 0 && (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                                    準確率 {accuracyRate}%
                                </span>
                            )}
                        </div>
                        <div className="p-0">
                            {recentHistory.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    <p>尚無測驗紀錄，快去挑戰吧！</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {recentHistory.map((record) => (
                                        <div key={record.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
                                            <div className="flex items-center space-x-4">
                                                <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center font-bold text-sm ${
                                                    record.score >= 90 ? 'bg-green-100 text-green-700' :
                                                    record.score >= 60 ? 'bg-blue-100 text-blue-700' :
                                                    'bg-red-100 text-red-700'
                                                }`}>
                                                    <span>{record.score}</span>
                                                    <span className="text-[10px] opacity-75">分</span>
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-800">{getFolderName(record.folderId)}</p>
                                                    <p className="text-xs text-gray-500 flex items-center mt-1">
                                                        <Calendar className="w-3 h-3 mr-1" />
                                                        {new Date(record.date).toLocaleString('zh-TW', { hour12: false, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right hidden sm:block">
                                                <span className="text-xs text-gray-400 block">總分 {record.totalPoints}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Suggestions / Tips */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                            <h3 className="font-bold text-gray-800 flex items-center">
                                <Trophy className="w-5 h-5 mr-2 text-yellow-500" />
                                學習建議
                            </h3>
                        </div>
                        <div className="p-6 flex-1 flex flex-col justify-center space-y-6">
                            {wrongQuestionIds.length > 0 ? (
                                <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                                    <h4 className="font-bold text-red-700 mb-2">專注弱點</h4>
                                    <p className="text-sm text-red-600 mb-3">
                                        你有 {wrongQuestionIds.length} 題錯題尚未掌握。複習錯題是提升分數最快的方法！
                                    </p>
                                    <button 
                                        onClick={() => onNavigate('mistake-book')}
                                        className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                        去複習
                                    </button>
                                </div>
                            ) : (
                                <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                                    <h4 className="font-bold text-green-700 mb-2">保持優秀</h4>
                                    <p className="text-sm text-green-600">
                                        目前沒有待複習的錯題，太棒了！可以嘗試挑戰新的單元，或者去「學習地圖」點亮更多成就。
                                    </p>
                                </div>
                            )}

                            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                <h4 className="font-bold text-fudan-blue mb-2">探索地圖</h4>
                                <p className="text-sm text-blue-800 mb-3">
                                    查看你的知識樹，看看哪些單元已經精通，哪些還需要加強。
                                </p>
                                <button 
                                    onClick={() => onNavigate('learning-map')}
                                    className="text-xs bg-fudan-blue text-white px-3 py-1.5 rounded-lg hover:bg-blue-800 transition-colors"
                                >
                                    前往地圖
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
